import { ObjectsDrawer } from './ObjectsDrawer';
import { AspObject, ObjectType } from './AspObject';
import { DisplayObject } from './DisplayObject';
import { IMapMngr, IObjectsDrawer, IObjectsPool } from './interfaces';
import { ObjectsPool } from './ObjectsPool';
import * as utils from '../common/utils';
import * as settings from '../common/settings';
import * as consts from '../common/consts';
import { aspMngr } from './AspMngr';
import { ColorPickerComponent } from '../common/color-picker/color-picker.component';

const DRAWING_LINE_WIDTH = 3;
const DRAWING_WIDE_LINE_WIDTH = 10;
const LINE_WIDTH_3D_FACTOR = 30;
const LOCAL_STORAGE_PROPERTIES_KEY = "Properties ";

//
// Inner classes
//

//
// Free drawing object
//
export class FreeDrawingObject extends AspObject {

  constructor(objectsPool: IObjectsPool, geometry: any, public color: string, public isWide: boolean) {
    super(objectsPool, objectsPool.getObjectType(), AspObject.generateId(objectsPool.getObjectType()));
    this.geometry = geometry;
    this.geometry.setZ(10);
  }

  getTitle(): string {
    return `FREE DRAWING`;
  }
  
  getColor(): number[] {
    return utils.cssColorToNewmi(this.color);
  }

  handleMsg(): boolean { return false; }
}

//
// Free drawings pool
//
export class FreeDrawingsPool extends ObjectsPool {

  private static _self: FreeDrawingsPool = null;

  public static getSelf(): FreeDrawingsPool {
    if (this._self == null)
      this._self = new FreeDrawingsPool();
    return this._self;
  }

  private constructor() {
    super(ObjectType.FreeDrawing, consts.MSG_TYPE_NON_SUPPORT, undefined, false, true /* clickable */, false);
    aspMngr.addObjectsPool(this);
    this.loadFromStorage();
  }
 
  // create free drawing object
  createFreeDrawing(geometry: any, color: string, isWide: boolean = false) {
    const newFreeDrawingObj = new FreeDrawingObject(this, geometry, color, isWide);
    this.addObject(newFreeDrawingObj);
    this.saveToStorage(newFreeDrawingObj);
  }

  createNatNaiDrawing(geometry: any, color: string, isWide: boolean = false) {
    const newFreeDrawingObj = new FreeDrawingObject(this, geometry, color, isWide);
    this.addObject(newFreeDrawingObj);
    return newFreeDrawingObj.id;
  }

  // delete freee drawing object
  deleteFreeDrawing(freeDrawingObj: FreeDrawingObject) {
    this.removeFromStorage(freeDrawingObj);
    this.deleteObject(freeDrawingObj);
  }

  deleteObj(freeDrawingObj: FreeDrawingObject) {
    this.deleteObject(freeDrawingObj);
  }  

  handleMsg(): void { }

  private saveToStorage(obj: FreeDrawingObject) {
    let objStr = obj.geometry.toJson();
    localStorage.setItem(obj.id, objStr);

    let props = { isWide: obj.isWide, color: obj.color };
    localStorage.setItem(LOCAL_STORAGE_PROPERTIES_KEY + obj.id, JSON.stringify(props));
  }

  private removeFromStorage(obj: FreeDrawingObject) {
    localStorage.removeItem(obj.id);
    localStorage.removeItem(LOCAL_STORAGE_PROPERTIES_KEY + obj.id);
  }

  private loadFromStorage() {
    for (let i = 0; i < localStorage.length; i++) {
      let key = localStorage.key(i);
      if (key.startsWith(this.objectType)) {
        let geometry = NeWMI.Geometry.Base.AGeometry.fromJson(localStorage.getItem(key));
        let props = JSON.parse(localStorage.getItem(LOCAL_STORAGE_PROPERTIES_KEY + key));
        let color = props ? props.color : ColorPickerComponent.defaultColor();
        let isWide = props ? props.isWide : false;
        const newFreeDrawingObj = new FreeDrawingObject(this, geometry, color, isWide);
        newFreeDrawingObj.id = key;
        this.addObject(newFreeDrawingObj);
      }
    }
  }
}

//
// Free drawing display object
//
class FreeDrawingDisplayObject extends DisplayObject {

  constructor(aspObject: AspObject, drawer: IObjectsDrawer) {
    super(aspObject, drawer);

    let freeDrawingObject = aspObject as FreeDrawingObject;

    this.geometry = this.aspObject.geometry.clone();
    if (drawer.is3D()) {
      // newmi-tbd
      let polyPoints = [];
      if (this.geometry.GeoType == NeWMI.Geometry.Enums.EGeometryType.Circle) {
        let circle = this.geometry as NeWMI.Geometry.Circle;
        polyPoints = NeWMI.Service.Math.InfoSvc.ellipseToRing(circle.x, circle.y, circle.radius, circle.radius, 0, 0);
      }
      else if (this.geometry.GeoType == NeWMI.Geometry.Enums.EGeometryType.Rectangle) {
        let rect = this.geometry as NeWMI.Geometry.Rectangle;
        polyPoints.push({ x: rect.xCenter - rect.width / 2, y: rect.yCenter - rect.height / 2 });
        polyPoints.push({ x: rect.xCenter + rect.width / 2, y: rect.yCenter - rect.height / 2 });
        polyPoints.push({ x: rect.xCenter + rect.width / 2, y: rect.yCenter + rect.height / 2 });
        polyPoints.push({ x: rect.xCenter - rect.width / 2, y: rect.yCenter + rect.height / 2 });
        polyPoints.push({ x: rect.xCenter - rect.width / 2, y: rect.yCenter - rect.height / 2 });
      }
      if (polyPoints.length > 0) {
        this.geometry = new NeWMI.Geometry.Polyline();
        for (let point of polyPoints)
          this.geometry.getPoints().push(new NeWMI.Geometry.Point({ x: point.x, y: point.y, z: this.aspObject.geometry.getZ() }));
      }
    }
    this.boundsRect = this.geometry.getEnvelope().getRect();

    let lineWidth = freeDrawingObject.isWide ? DRAWING_WIDE_LINE_WIDTH : DRAWING_LINE_WIDTH;
    if (drawer.is3D()) // newmi-tbd
      lineWidth *= LINE_WIDTH_3D_FACTOR;
    let color = aspObject.getColor();

    if (drawer.is3D()) {
      this.drawData[aspObject.objectType].location = this.geometry;
      this.drawData[aspObject.objectType].lineWidth = lineWidth;
      this.drawData[aspObject.objectType].color = color;
    }
    else {
      this.drawData[aspObject.objectType].geometry = this.geometry;
      this.drawData[aspObject.objectType].line = { width: lineWidth, color: color };
    }

    this.drawer.addObjectToDm(this);
    this.drawer.getMapMngr().redrawMap();
  }

}

//
// Free drawings drawer
//
export class FreeDrawingsDrawer extends ObjectsDrawer {

  constructor(mapMngr: IMapMngr, parentLayer) {
    super(mapMngr, FreeDrawingsPool.getSelf(), null, parentLayer, null, null, null, null);
    this.objectsLayer.customSearch.push(this.customSearchFunc.bind(this));
  }

  isLayerVisible(): boolean {
    return settings.userSettings.mapElementsOn[consts.MAP_DISPLAY_ELEMENT_FREE_DRAWINGS];
  }

  protected addDrawTypes() {
    this.addGeometryDrawType(this.objectsType, { properties: NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor }, { enabled: true, selectable: true });
  }

  createDisplayObject(obj: AspObject): DisplayObject {
    const newFreeDrawing = new FreeDrawingDisplayObject(obj, this);
    return newFreeDrawing;
  }

  onObjectDelete(obj: AspObject): void {
    // notified of free drawing object deletion
    super.onObjectDelete(obj);
    this.mapMngr.redrawMap();
  }
  
  // Make sure the geometry is hit on its boundary and not its content
  private customSearchFunc (sender: NeWMI.Layer.Base.ACustomObjectsLayer, objGeo: NeWMI.Geometry.Rectangle, exactSearch: boolean,
    currSearchedObjects: NeWMI.Object.NeWMIObject[]): NeWMI.Object.NeWMIObject[] {
    let hitObjects: NeWMI.Object.NeWMIObject[] = [];
    let hitPoint = new NeWMI.Geometry.Point({ x: objGeo.xCenter, y: objGeo.yCenter });

    for (let i = 0; i < currSearchedObjects.length; i++) {
      let obj = currSearchedObjects[i];
      let result = obj.geometry.hitTest(this.mapMngr.getMap(), hitPoint);
      let remove = result.hitResult != NeWMI.Geometry.Enums.EHitState.vertex && result.hitResult != NeWMI.Geometry.Enums.EHitState.boundary;
      if (remove) {
        // no vertex/boundary hit
        currSearchedObjects.splice(i, 1);
        i--;
      }
    }

    return hitObjects;
  }

}
